import _ from 'lodash';
import {
  EVIDENCE_CONTEXT_LIMIT,
  GRAPHQL_ITEM_PROPERTIES_TO_INCLUDE_LABELS,
  GRAPHQL_ITEM_PROPERTIES_TO_INCLUDE_NUMERICS,
  GRAPHQL_ITEM_PROPERTIES_TO_INCLUDE_OPAQUE,
  LONG_AGO,
  VantageNames,
} from '@/vantage/constants';
import { sqGraphQLApi } from '@/sdk';
import { AnyProperty } from '@/utilities.types';
import { Moment } from 'moment';
import { NUMBER_CONVERSIONS } from '@/main/app.constants';
import { SeeqAxiosRequestConfig } from '@/requests/axios.utilities';
import { SeeqNames } from '@/main/app.constants.seeqnames';
import { Filter, GraphQL } from '@/serverSideTable/materializedTables/materializedTables.constants';
import {
  andFilters,
  equal,
  isNull,
  notEqual,
  orFilters,
  simpleFilter,
  valueFilter,
} from '@/serverSideTable/materializedTables/filters';
import { GroupBy } from '@/serverSideTable/serverSideTable.constants';
import { IdBundle } from '@/vantage/components/extensionviews/reasonCodes.utilities';
import { LabelHierarchyGraphQLTreeResponse, LabelHierarchyInputPair } from '@/vantage/types/Context.types';

const getAllContext = `
query Context($ids: [ContextIdInput!]!, $labelFilter: FilterInput, $commentFilter: FilterInput, $opaqueFilter: FilterInput) {
  context(ids: $ids, labelFilter: $labelFilter, commentFilter: $commentFilter, opaqueFilter: $opaqueFilter) {
    opaque {
      itemId
      datumId
      key
      context
      contextId
      createdAt
      creatorId
      updatedAt
      archived
      startTime
      endTime
      item{
        type
      }
      createdBy {
        guid
        fullName
      }
    }
    comments {
      itemId
      datumId
      context
      contextId
      createdAt
      updatedAt
      archived
      startTime
      endTime
      isPinned
      item{
        type
      }
      createdBy {
        guid
        fullName
      }
    }
    labels {
      itemId
      datumId
      context
      contextId
      createdAt
      creatorId
      updatedAt
      archived
      label
      categoryName
      startTime
      endTime
      item{
       type
      }
      createdBy {
        guid
        fullName
      }
    }
    numerics {
      itemId
      datumId
      context
      contextId
      createdAt
      creatorId
      updatedAt
      archived
      numericDescriptionName
      value
      startTime
      endTime
      item{
       type
      }
      createdBy {
        guid
        fullName
      }
    }
  }
}
`;

const getLabelContext = `
query Context($ids: [ContextIdInput!]!, $labelFilter: FilterInput) {
  context(ids: $ids, labelFilter: $labelFilter) {
    labels {
      itemId
      datumId
      context
      contextId
      createdAt
      creatorId
      updatedAt
      archived
      label
      categoryName
      startTime
      endTime
      item{
       type
      }
      createdBy {
        guid
        fullName
      }
    }
  }
}
`;

const getOpaqueContext = `
query Context($ids: [ContextIdInput!]!) {
  context(ids: $ids) {
    opaque {
      itemId
      datumId
      key
      context
      contextId
      archived
      startTime
      endTime
    }
  }
}
`;

const getLabelHierarchiesQuery = `
query LabelHierarchyTree($input: LabelHierarchiesInput!) {
  labelHierarchyTree(input: $input)
}
`;

const getTableQuery = `
query GetTable($id: String!, $filter: FilterInput, $limit: Int!, $columnsToInclude: [String!], $propertiesToInclude: [PropertiesForItemUUIDColumnInput!], $sorts: [SortInput!], $cursor: CursorInput, $useDistinct: Boolean, $virtualColumns: VirtualColumnsInput, $groupBy: GroupByInput) {
  table(id: $id, filter: $filter, limit: $limit, columnsToInclude: $columnsToInclude, propertiesToInclude: $propertiesToInclude, sorts: $sorts, cursor: $cursor, useDistinct: $useDistinct, virtualColumns: $virtualColumns, groupBy: $groupBy) {
    rows
    headers {
      name
      type
    }
    hasMore,
    cursorKey
  }
}
`;

export { getTableQuery };

interface TableData {
  headers: { name: string; type: string }[];
  rows: any[][];
  cursorKey?: string;
}

interface ContextData {
  opaque: AnyProperty[];
  comments: AnyProperty[];
  labels: AnyProperty[];
  numerics: AnyProperty[];
}

interface LabelsData {
  labels: AnyProperty[];
}

export function tabulate({ rows, headers }: TableData): AnyProperty[] {
  const headerNames = headers.map((h) => h.name);
  return rows.map((row) => _.zipObject(headerNames, row));
}

async function graphql<T>(query: string, variables: AnyProperty, cancellationGroup?: string): Promise<T> {
  const config: SeeqAxiosRequestConfig = {};
  if (cancellationGroup) {
    config.cancellationGroup = cancellationGroup;
    config.cancelOnServer = true;
  }
  const {
    data: { errors, data },
  } = await sqGraphQLApi.graphql({ query, variables }, config);
  if (errors) {
    throw Error(JSON.stringify(errors));
  } else {
    return data;
  }
}

export async function getTable(
  tableId: string,
  {
    limit,
    columns,
    properties,
    filter,
    sorts,
    cursor,
    useDistinct,
    virtualColumns,
    groupBy,
  }: {
    limit?: number;
    columns: string[];
    properties?: AnyProperty[];
    filter?: any;
    sorts?: { columnName: string; ascending: boolean }[];
    cursor?: { cursorKey: string };
    useDistinct?: boolean;
    virtualColumns?: { vantage: string[] };
    groupBy?: GroupBy;
  },
  cancellationGroup?: string,
) {
  const data = await graphql<{ table: TableData }>(
    getTableQuery,
    {
      id: tableId,
      limit,
      columnsToInclude: columns,
      propertiesToInclude: properties,
      filter,
      sorts,
      cursor,
      useDistinct,
      virtualColumns,
      groupBy,
    },
    cancellationGroup,
  );

  return { data: tabulate(data.table), cursorKey: data.table.cursorKey };
}

interface Event {
  properties: { name: string; value: any; unitOfMeasure?: string }[];
}

export function publish(tableId: string, events: Event[]) {
  return graphql('mutation PublishEvents($rowEvents: RowEventsInput!) { publishEvent(rowEvents: $rowEvents) }', {
    rowEvents: { tableDefinitionId: tableId, events: events.map((event) => ({ genericEvent: event })) },
  });
}

export async function context(itemId: string, datumId: string | undefined) {
  const data = await graphql<{ context: ContextData }>(getOpaqueContext, { ids: [{ itemId, datumIds: [datumId] }] });

  return data.context.opaque;
}

async function fetchContextForIdPairs<T>(
  idPairs: ([string, string] | [string])[],
  query: string,
  emptyResult: T,
  filters?: { labelFilter?: Filter; commentFilter?: Filter; opaqueFilter?: Filter; numericFilter?: Filter },
  cancellationGroup?: string,
): Promise<T> {
  if (idPairs.length === 0) {
    return emptyResult;
  }
  const ids = _.chain(idPairs)
    .groupBy((idPair) => idPair[0])
    .map((idPairs, itemId) => {
      const datumIds = idPairs.flatMap((idPair) => idPair[1]).filter((datumId) => datumId);
      return {
        itemId,
        datumIds: datumIds.length > 0 ? datumIds : undefined,
      };
    })
    .value();

  const { labelFilter, commentFilter, opaqueFilter, numericFilter } = filters ?? {};
  const data = await graphql(
    query,
    { ids, labelFilter, commentFilter, opaqueFilter, numericFilter },
    cancellationGroup,
  );
  return (data as { context: T }).context;
}

export async function allContextForIdPairs(
  idPairs: ([string, string] | [string])[],
  datumIds?: string[],
  cancellationGroup?: string,
): Promise<ContextData> {
  const labelFilter = datumIds
    ? orFilters(
        simpleFilter(VantageNames.TableQueryContextColumns.ContextLabelsDatumId, GraphQL.FilterTypes.IsNull),
        valueFilter(VantageNames.TableQueryContextColumns.ContextLabelsDatumId, GraphQL.FilterTypes.In, datumIds),
      )
    : undefined;

  const commentFilter = datumIds
    ? orFilters(
        simpleFilter(VantageNames.TableQueryContextColumns.ContextCommentsDatumId, GraphQL.FilterTypes.IsNull),
        valueFilter(VantageNames.TableQueryContextColumns.ContextCommentsDatumId, GraphQL.FilterTypes.In, datumIds),
      )
    : undefined;

  const opaqueFilter = datumIds
    ? orFilters(
        simpleFilter(VantageNames.TableQueryContextColumns.ContextOpaqueDatumId, GraphQL.FilterTypes.IsNull),
        valueFilter(VantageNames.TableQueryContextColumns.ContextOpaqueDatumId, GraphQL.FilterTypes.In, datumIds),
      )
    : undefined;

  const numericFilter = datumIds
    ? orFilters(
        simpleFilter(VantageNames.TableQueryContextColumns.ContextNumericDatumId, GraphQL.FilterTypes.IsNull),
        valueFilter(VantageNames.TableQueryContextColumns.ContextNumericDatumId, GraphQL.FilterTypes.In, datumIds),
      )
    : undefined;

  return fetchContextForIdPairs<ContextData>(
    idPairs,
    getAllContext,
    { opaque: [], comments: [], labels: [], numerics: [] },
    { labelFilter, commentFilter, opaqueFilter, numericFilter },
    cancellationGroup,
  );
}

export async function labelContextForIdPairs(
  idPairs: ([string, string] | [string])[],
  filters?: { labelFilter?: Filter; commentFilter?: Filter; opaqueFilter?: Filter },
  cancellationGroup?: string,
): Promise<LabelsData> {
  return fetchContextForIdPairs<LabelsData>(idPairs, getLabelContext, { labels: [] }, filters, cancellationGroup);
}

export function evidenceFilter(
  start: Moment,
  end: Moment,
  includeExtinct: boolean,
  includeArchived: boolean,
  lastFetched: Date = LONG_AGO,
) {
  return andFilters(
    valueFilter(
      VantageNames.Columns.Start,
      GraphQL.FilterTypes.LessThan,
      end.valueOf() * NUMBER_CONVERSIONS.NANOSECONDS_PER_MILLISECOND,
    ),
    orFilters(
      isNull(VantageNames.Columns.End),
      valueFilter(
        VantageNames.Columns.End,
        GraphQL.FilterTypes.GreaterThan,
        start.valueOf() * NUMBER_CONVERSIONS.NANOSECONDS_PER_MILLISECOND,
      ),
    ),
    valueFilter(VantageNames.Columns.UpdatedAt, GraphQL.FilterTypes.GreaterThanOrEqual, lastFetched.toISOString()),
    includeArchived ? null : isNull(VantageNames.Columns.DeletedAt),
    includeExtinct ? null : notEqual(VantageNames.Columns.LastCapsuleState, VantageNames.ColumnValues.Extinct),
  );
}

function evidenceContextFilter(
  start: Moment,
  end: Moment,
  lastFetched: Date,
  includeExtinct: boolean,
  updatedAtColumn: string,
  archivedColumn: string,
  includeArchived = false,
) {
  return andFilters(
    evidenceFilter(start, end, includeExtinct, includeArchived),
    valueFilter(updatedAtColumn, GraphQL.FilterTypes.GreaterThanOrEqual, lastFetched.toISOString()),
    includeArchived ? null : equal(archivedColumn, false),
  );
}

export function evidenceLabelsFilter(
  start: Moment,
  end: Moment,
  lastFetched: Date,
  includeExtinct: boolean,
  includeArchived = false,
) {
  return evidenceContextFilter(
    start,
    end,
    lastFetched,
    includeExtinct,
    VantageNames.TableQueryContextColumns.ContextLabelsUpdatedAt,
    VantageNames.TableQueryContextColumns.ContextLabelsArchived,
    includeArchived,
  );
}

export function evidenceNumericsFilter(
  start: Moment,
  end: Moment,
  lastFetched: Date,
  includeExtinct: boolean,
  includeArchived = false,
) {
  return evidenceContextFilter(
    start,
    end,
    lastFetched,
    includeExtinct,
    VantageNames.TableQueryContextColumns.ContextNumericUpdatedAt,
    VantageNames.TableQueryContextColumns.ContextNumericArchived,
    includeArchived,
  );
}

export function evidenceOpaqueFilter(
  start: Moment,
  end: Moment,
  lastFetched: Date,
  includeExtinct: boolean,
  includeArchived = false,
) {
  return evidenceContextFilter(
    start,
    end,
    lastFetched,
    includeExtinct,
    VantageNames.TableQueryContextColumns.ContextOpaqueUpdatedAt,
    VantageNames.TableQueryContextColumns.ContextOpaqueArchived,
    includeArchived,
  );
}

export async function evidenceLabels(evidenceTableId: string, filter: Filter, cancellationGroup?: string) {
  const { data } = await getTable(
    evidenceTableId,
    {
      limit: EVIDENCE_CONTEXT_LIMIT,
      columns: [
        SeeqNames.MaterializedTables.ItemIdColumn,
        SeeqNames.MaterializedTables.DatumIdColumn,
        VantageNames.TableQueryContextColumns.ContextLabelsId,
        VantageNames.TableQueryContextColumns.ContextLabelsLabelId,
        VantageNames.TableQueryContextColumns.ContextLabelsLabelName,
        VantageNames.TableQueryContextColumns.ContextLabelsCategoryName,
        VantageNames.TableQueryContextColumns.ContextLabelsCreatedAt,
        VantageNames.TableQueryContextColumns.ContextLabelsUpdatedAt,
        VantageNames.TableQueryContextColumns.ContextLabelsArchived,
        VantageNames.TableQueryContextColumns.ContextLabelsCreatorId,
      ],
      filter,
      properties: GRAPHQL_ITEM_PROPERTIES_TO_INCLUDE_LABELS,
    },
    cancellationGroup ? `${cancellationGroup}-evidenceLabels` : undefined,
  );
  return data;
}

export async function evidenceNumerics(evidenceTableId: string, filter: Filter, cancellationGroup?: string) {
  const { data } = await getTable(
    evidenceTableId,
    {
      limit: EVIDENCE_CONTEXT_LIMIT,
      columns: [
        SeeqNames.MaterializedTables.ItemIdColumn,
        SeeqNames.MaterializedTables.DatumIdColumn,
        VantageNames.TableQueryContextColumns.ContextNumericId,
        VantageNames.TableQueryContextColumns.ContextNumericValue,
        VantageNames.TableQueryContextColumns.ContextNumericNumericDescriptionId,
        VantageNames.TableQueryContextColumns.ContextNumericNumericDescriptionName,
        VantageNames.TableQueryContextColumns.ContextNumericStartTime,
        VantageNames.TableQueryContextColumns.ContextNumericEndTime,
        VantageNames.TableQueryContextColumns.ContextNumericCreatedAt,
        VantageNames.TableQueryContextColumns.ContextNumericUpdatedAt,
        VantageNames.TableQueryContextColumns.ContextNumericArchived,
        VantageNames.TableQueryContextColumns.ContextNumericCreatorId,
      ],
      filter,
      properties: GRAPHQL_ITEM_PROPERTIES_TO_INCLUDE_NUMERICS,
    },
    cancellationGroup ? `${cancellationGroup}-evidenceLabels` : undefined,
  );
  return data;
}

export async function evidenceOpaque(evidenceTableId: string, filter: Filter, cancellationGroup?: string) {
  const { data } = await getTable(
    evidenceTableId,
    {
      limit: EVIDENCE_CONTEXT_LIMIT,
      columns: [
        SeeqNames.MaterializedTables.ItemIdColumn,
        SeeqNames.MaterializedTables.DatumIdColumn,
        VantageNames.TableQueryContextColumns.ContextOpaqueId,
        VantageNames.TableQueryContextColumns.ContextOpaqueData,
        VantageNames.TableQueryContextColumns.ContextOpaqueKey,
        VantageNames.TableQueryContextColumns.ContextOpaqueStartTime,
        VantageNames.TableQueryContextColumns.ContextOpaqueEndTime,
        VantageNames.TableQueryContextColumns.ContextOpaqueCreatedAt,
        VantageNames.TableQueryContextColumns.ContextOpaqueUpdatedAt,
        VantageNames.TableQueryContextColumns.ContextOpaqueArchived,
        VantageNames.TableQueryContextColumns.ContextOpaqueCreatorId,
      ],
      filter,
      properties: GRAPHQL_ITEM_PROPERTIES_TO_INCLUDE_OPAQUE,
    },
    cancellationGroup ? `${cancellationGroup}-evidenceOpaque` : undefined,
  );
  return data;
}

export async function labelHierarchies(categoryLabelPairs: LabelHierarchyInputPair[]): Promise<IdBundle[]> {
  const variables = {
    input: {
      pairs: categoryLabelPairs,
      depth: 10,
      includeArchived: false,
      maxNodes: 10000,
    },
  };
  const response: LabelHierarchyGraphQLTreeResponse = await graphql(
    getLabelHierarchiesQuery,
    variables,
    'labelHierarchies',
  );
  return response.labelHierarchyTree?.hierarchies ?? [];
}
